import React, { useState, useMemo } from 'react';
import { Modal, TextInput } from '@gravity-ui/uikit';
import { MKFBrowserModalProps, MKFCode } from './types';
import { MKFCodeItem } from './mkf-code-item';
import { useGetMKFCodesQuery } from '../../../../../store/api';
import styles from './mkf-browser-modal.module.scss';

export const MKFBrowserModal: React.FC<MKFBrowserModalProps> = ({ isOpen, onClose, onSelect, selectedCode }) => {
	const [searchQuery, setSearchQuery] = useState('');
	const [tempSelectedCode, setTempSelectedCode] = useState<string | undefined>(selectedCode);
	const [tempSelectedName, setTempSelectedName] = useState<string | undefined>(undefined);
	const { data: mkfCodes = [], isLoading } = useGetMKFCodesQuery();

	const filteredCodes = useMemo(() => {
		if (!searchQuery.trim()) {
			return mkfCodes as unknown as MKFCode[];
		}

		const searchLower = searchQuery.toLowerCase();

		const filterCodes = (codes: MKFCode[]): MKFCode[] => {
			return codes.reduce((acc: MKFCode[], code) => {
				const matchesSearch =
					code.code.toLowerCase().includes(searchLower) || code.name.toLowerCase().includes(searchLower);

				if (matchesSearch) {
					acc.push(code);
				} else if (code.codes.length > 0) {
					const filteredChildren = filterCodes(code.codes);
					if (filteredChildren.length > 0) {
						acc.push({
							...code,
							codes: filteredChildren,
						});
					}
				}

				return acc;
			}, []);
		};

		return filterCodes(mkfCodes as unknown as MKFCode[]);
	}, [searchQuery, mkfCodes]);

	const handleSelect = (code: string, name?: string) => {
		setTempSelectedCode(code);
		setTempSelectedName(name);
	};

	const handleConfirm = () => {
		if (tempSelectedCode) {
			onSelect(tempSelectedCode, tempSelectedName);
		}
		onClose();
	};

	const handleCancel = () => {
		setTempSelectedCode(selectedCode);
		onClose();
	};

	return (
		<Modal open={isOpen} onClose={onClose} className={styles.modal}>
			<div className={styles.modalContent}>
				<div className={styles.header}>
					<h2 className={styles.title}>ICF Browser</h2>
					<button className={styles.closeButton} onClick={onClose}>
						✕
					</button>
				</div>

				<div className={styles.searchContainer}>
					<TextInput
						placeholder="Поиск по МКФ-коду от 1 символа"
						value={searchQuery}
						onUpdate={setSearchQuery}
						hasClear
						size="xl"
						className={styles.searchInput}
					/>
				</div>

				<div className={styles.listContainer}>
					{isLoading && <div>Загрузка...</div>}
					{!isLoading &&
						filteredCodes.map((item) => (
							<MKFCodeItem
								key={item.code}
								item={item}
								level={0}
								onSelect={handleSelect}
								selectedCode={tempSelectedCode}
							/>
						))}
				</div>

				<div className={styles.footer}>
					<button onClick={handleCancel} className={styles.cancelButton}>
						Отменить
					</button>
					<button
						onClick={handleConfirm}
						disabled={!tempSelectedCode}
						className={`${styles.confirmButton} ${tempSelectedCode ? styles.confirmEnabled : styles.confirmDisabled}`}
					>
						Выбрать код
					</button>
				</div>
			</div>
		</Modal>
	);
};
